import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Newspaper, Users, Mail, LogOut, 
  ChevronLeft, Menu, X, Database, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logout, getAuthUser } from '@/lib/api';

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/edicoes', icon: Newspaper, label: 'Edições' },
  { path: '/admin/usuarios', icon: Users, label: 'Usuários', masterOnly: true },
  { path: '/admin/newsletter', icon: Mail, label: 'Newsletter' },
  { path: '/admin/certificados', icon: Shield, label: 'Certificados', masterOnly: true },
  { path: '/admin/backup', icon: Database, label: 'Backup', masterOnly: true },
];

export const AdminLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const user = getAuthUser();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check auth
  React.useEffect(() => {
    const checkAuth = () => {
      const storedUser = getAuthUser();
      if (!storedUser) {
        navigate('/login');
      }
      setIsLoading(false);
    };
    checkAuth();
  }, [navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-700"></div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="admin-layout">
      {/* Mobile Header */}
      <div className="lg:hidden bg-[#1F2937] text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-white/20 rounded-full blur-md scale-110"></div>
            <img 
              src="/logo_doem.png" 
              alt="DEMOAC"
              className="h-10 w-auto relative z-10"
            />
          </div>
          {/* Texto DEMOAC com brilho */}
          <div className="relative flex flex-col justify-center">
            <div className="absolute inset-0 bg-[#FFD700]/15 blur-lg scale-110 rounded-lg"></div>
            <span className="relative z-10 text-[#FFD700] font-heading font-bold text-sm tracking-wide drop-shadow-[0_0_10px_rgba(255,215,0,0.7)]" style={{textShadow: '0 0 15px rgba(255,215,0,0.8)'}}>
              DEMOAC
            </span>
            <span className="relative z-10 text-[#FFD700] font-heading text-[7px] tracking-wider drop-shadow-[0_0_8px_rgba(255,215,0,0.5)]" style={{textShadow: '0 0 10px rgba(255,215,0,0.6)'}}>
              DIÁRIO ELETRÔNICO MUNICIPAL DE ACAIACA
            </span>
          </div>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#1F2937] text-white p-4 space-y-2">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            
            // Check if user can access master-only items
            if (item.masterOnly && !user.is_master) {
              return null;
            }
            
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  isActive ? 'bg-white/20 text-white' : 'text-gray-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                <Icon size={20} />
                {item.label}
              </Link>
            );
          })}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 p-3 rounded-lg text-gray-400 hover:bg-white/10 hover:text-white w-full"
          >
            <LogOut size={20} />
            Sair
          </button>
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside 
          className={`hidden lg:flex flex-col bg-[#1F2937] text-white transition-all duration-300 ${
            sidebarOpen ? 'w-72' : 'w-20'
          }`}
          style={{ minHeight: 'calc(100vh - 0px)' }}
        >
          {/* Logo com texto DEMOAC */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center gap-3 justify-center">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-white/20 rounded-full blur-lg scale-110"></div>
                <img 
                  src="/logo_doem.png" 
                  alt="DEMOAC"
                  className="h-14 w-auto relative z-10"
                />
              </div>
              {sidebarOpen && (
                <div className="relative flex flex-col justify-center">
                  <div className="absolute inset-0 bg-[#FFD700]/15 blur-lg scale-110 rounded-lg"></div>
                  <span className="relative z-10 text-[#FFD700] font-heading font-bold text-lg tracking-wide drop-shadow-[0_0_12px_rgba(255,215,0,0.7)]" style={{textShadow: '0 0 18px rgba(255,215,0,0.8), 0 0 35px rgba(255,215,0,0.4)'}}>
                    DEMOAC
                  </span>
                  <span className="relative z-10 text-[#FFD700] font-heading text-[8px] tracking-wider drop-shadow-[0_0_8px_rgba(255,215,0,0.5)]" style={{textShadow: '0 0 12px rgba(255,215,0,0.6)'}}>
                    DIÁRIO ELETRÔNICO MUNICIPAL
                  </span>
                  <span className="relative z-10 text-[#FFD700] font-heading text-[8px] tracking-wider drop-shadow-[0_0_8px_rgba(255,215,0,0.5)]" style={{textShadow: '0 0 12px rgba(255,215,0,0.6)'}}>
                    DE ACAIACA
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              
              // Check if user can access master-only items
              if (item.masterOnly && !user.is_master) {
                return null;
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-[#B91C1C] text-white' 
                      : 'text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                  title={!sidebarOpen ? item.label : undefined}
                >
                  <Icon size={20} />
                  {sidebarOpen && item.label}
                </Link>
              );
            })}
          </nav>

          {/* User & Logout */}
          <div className="p-4 border-t border-gray-700">
            {sidebarOpen && (
              <div className="mb-3">
                <p className="font-medium truncate">{user.nome}</p>
                <p className="text-xs text-gray-400 truncate">{user.email}</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="text-gray-400 hover:text-white hover:bg-white/10"
              >
                <ChevronLeft size={20} className={`transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-gray-400 hover:text-white hover:bg-white/10"
                title="Sair"
              >
                <LogOut size={20} />
              </Button>
              <Link to="/">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-400 hover:text-white hover:bg-white/10"
                  title="Ver Site"
                >
                  <Newspaper size={20} />
                </Button>
              </Link>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto" style={{ minHeight: 'calc(100vh - 64px)' }}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
